/**
 * Extracts a human-readable error message from an HTTP response object.
 * This function tries to find error messages in various common fields used by the backend.
 * 
 * @param response - The HTTP response object from an API call
 * @param defaultMessage - The default message to return if no specific error is found
 * @returns A string containing the error message
 */
export const extractErrorMessage = (response: any, defaultMessage: string = "An Error Occurred"): string => {
  if (!response) {
    return defaultMessage;
  }

  // If response has a parsedBody with error information
  if (response.parsedBody && typeof response.parsedBody === 'object') {
    const errorBody = response.parsedBody as any;
    
    // Try different common error message fields in order of preference
    if (errorBody.message) {
      return errorBody.message;
    }
    
    if (errorBody.error) {
      // Handle case where error is a string
      if (typeof errorBody.error === 'string') {
        return errorBody.error;
      }
      
      // Handle case where error is an object with nested properties
      if (typeof errorBody.error === 'object') {
        if (errorBody.error.message) {
          return errorBody.error.message;
        }
        if (errorBody.error.description) {
          return errorBody.error.description;
        }
        if (errorBody.error.code) {
          return errorBody.error.code;
        }
      }
    }
    
    // Try other common error fields
    if (errorBody.errorMessage) {
      return errorBody.errorMessage;
    }
    
    if (errorBody.detail) {
      return errorBody.detail;
    }
  }
  
  // Fall back to HTTP status text if available
  if (response.statusText && response.statusText !== 'Unknown') {
    return response.statusText;
  }
  
  // Include HTTP status code in the message if available
  if (response.status && response.status !== 200) {
    return `${defaultMessage} (HTTP ${response.status})`;
  }
  
  return defaultMessage;
};
